import {
  Component, Mixins, Watch,
} from 'vue-property-decorator';
import { State } from 'vuex-class';
import { uniqBy } from 'lodash';
import { PlatformType } from '@/store/directory/types';
import FilterBuilder from '@/classes/pimcore-filtering/FilterBuilder';
import { and, like } from '@/classes/pimcore-filtering/Filters';
import LoadingDataMixin from '@/mixins/LoadingDataMixin';
import { TypeExtractionRules } from '@/classes/schema-constructors/types';
import UserMixin from '@/mixins/UserMixin';

@Component
export default class PlatformsAndSuppliersMixin extends Mixins(LoadingDataMixin, UserMixin) {
  @State('platforms', { namespace: 'directory' })
  readonly platforms!: PlatformType[];

  @State('platformsIsLoading', { namespace: 'directory' })
  readonly platformsIsLoading!: boolean;

  suppliers = [];

  suppliersIsLoading = false;

  clients: any[] = [];

  clientsIsLoading = false;

  selectedPlatform: string | number | (string | number)[] | null = '';

  /**
   * Normalized platform id list for supplier fetch (single id or multi-select array).
   */
  getSelectedPlatformIds(): string[] {
    const v = this.selectedPlatform as string | number | (string | number)[] | null | undefined;
    if (v === null || v === undefined || v === '') {
      return [];
    }
    if (Array.isArray(v)) {
      return v.map((x) => String(x)).filter((s) => s !== '');
    }
    return [String(v)];
  }

  get hasSelectedPlatforms(): boolean {
    return this.getSelectedPlatformIds().length > 0;
  }

  async loadSuppliers(rules?: TypeExtractionRules): Promise<any[]> {
    const ids = this.getSelectedPlatformIds();
    if (ids.length === 0) {
      this.suppliers = [];
      return [];
    }
    if (ids.length === 1) {
      return this.loadDataByLike('suppliers', 'Supplier', 'PlantsHR', ids[0], 'cache-first', rules);
    }
    this.suppliersIsLoading = true;
    try {
      const rowsPerPlatform = await Promise.all(ids.map((id) => {
        const filterBuilder = new FilterBuilder();
        filterBuilder.addFilters(
          and(
            like('PlantsHR', `%${id}%`),
          ),
        );
        return this.loadEntityListing('Supplier', {
          filter: filterBuilder.build(),
        }, 'cache-first', rules);
      }));
      const merged = rowsPerPlatform.flatMap((rows) => rows || []);
      this.suppliers = uniqBy(merged, 'id') as [];
      return this.suppliers;
    } catch (e) {
      console.error(e);
      this.suppliers = [];
      return [];
    } finally {
      this.suppliersIsLoading = false;
    }
  }

  async loadClients(rules?: TypeExtractionRules): Promise<any[]> {
    const ids = this.getSelectedPlatformIds();
    if (ids.length === 0) {
      this.clients = [];
      return [];
    }
    if (ids.length === 1) {
      return this.loadDataByLike('clients', 'Client', 'Plants', ids[0], 'cache-first', rules);
    }
    this.clientsIsLoading = true;
    try {
      const rowsPerPlatform = await Promise.all(ids.map((id) => {
        const filterBuilder = new FilterBuilder();
        filterBuilder.addFilters(
          and(
            like('Plants', `%${id}%`),
          ),
        );
        return this.loadEntityListing('Client', {
          filter: filterBuilder.build(),
        }, 'cache-first', rules);
      }));
      const merged = rowsPerPlatform.flatMap((rows) => rows || []);
      this.clients = uniqBy(merged, 'id') as [];
      return this.clients;
    } catch (e) {
      console.error(e);
      this.clients = [];
      return [];
    } finally {
      this.clientsIsLoading = false;
    }
  }

  /**
   * Стартовая платформа после загрузки справочника.
   * На странице «Формирование листов учета» переопределяется (одна платформа / пусто, без storage).
   */
  initializePlatformFromUserAndStorage() {
    let selectedPlatform = '';
    if (typeof window !== 'undefined') {
      selectedPlatform = this.getFromStorage('selected_platform');
    }
    if (Number(selectedPlatform)) {
      this.selectedPlatform = selectedPlatform;
    } else {
      this.selectedPlatform = this.userPlatforms[0]?.id || '';
    }
  }

  afterLoadingPlatforms() {
    console.log('platforms loaded');
  }

  async beforeLoadSuppliers() {
    console.log('before load suppliers');
  }

  async afterLoadSuppliers(meta?: { platformSwitched?: boolean }) {
    void meta;
    console.log('after load suppliers');
  }

  @Watch('selectedPlatform', { deep: true })
  async selectedPlatformChanged(_value: unknown, oldValue: unknown) {
    const ids = this.getSelectedPlatformIds();
    if (ids.length === 0) {
      this.suppliers = [];
      return;
    }
    let oldKey = '';
    if (Array.isArray(oldValue)) {
      oldKey = [...oldValue].map(String).sort().join(',');
    } else if (oldValue !== undefined && oldValue !== null && oldValue !== '') {
      oldKey = String(oldValue);
    }
    const newKey = [...ids].sort().join(',');
    const platformSwitched = oldKey !== '' && oldKey !== newKey;
    if (platformSwitched) {
      await this.beforeLoadSuppliers();
    }
    if (typeof window !== 'undefined') {
      this.setToStorage('selected_platform', ids.length > 1 ? ids.join(',') : ids[0]);
    }
    await this.loadSuppliers({ maxDepth: 1 });
    await this.afterLoadSuppliers({ platformSwitched });
  }

  @Watch('platformsIsLoading', {
    immediate: true,
  })
  async platformsIsLoadingChanged(value: string) {
    if (!value) {
      this.initializePlatformFromUserAndStorage();
      this.afterLoadingPlatforms();
    }
  }
}
